import { getMyInfoData } from "../../utils/myinfoData";
import { netrustTrialResolveRequest, hasNetrustTrialContext, isNetrustTrialCallback } from "./netrustTrial";
import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import Cookies from "js-cookie";
import { format, isValid } from "date-fns";
import CrmSyncReview from "./CrmSyncReview";
import PropLoanConfirmation from "./proploanconfirmation";
import PropertyCoOwnerMyinfo from "./PropertyCoOwnerMyinfo";

const BauthConf = () => {
  const history = useHistory();
  const isPropertyCoOwnerCallback = new URLSearchParams(window.location.search).get("state")?.startsWith("slprop_");
  const [privacyBar, setPrivacybar] = useState(true);
  const [screenWidth, setScreenWidth] = useState(1920);

  const [myinfoData, setMyinfoData] = useState(null);
  const [trialSessionId, setTrialSessionId] = useState(null);
  const [myinfoError, setMyinfoError] = useState(false); // false | 'NAME_MISMATCH' | 'COOKIE_MISSING' | 'GENERIC'
  const [busyConfirm, setBusyConfirm] = useState(false);
  const [crmSync, setCrmSync] = useState(null);
  const [crmEntityData, setCrmEntityData] = useState(null);
  const [crmBusinessData, setCrmBusinessData] = useState(null);

  const MONEY_LENDER_CAMPAIGN_COOKIE = "money_lender_campaign_data";
  const CRM_STATE_PREFIX = "slcrm_";

  // ---- Flow detection via cookies ----
  const isOtherOwnerFlow = Cookies.get("bauthtype") === "proploanconfirmation" && !isNetrustTrialCallback();

  const clearCoOwnerCookies = () => {
    const rm = (k) => Cookies.remove(k, { path: "/" });
    rm("bauthtype");
    rm("bauthname");
    rm("myinfo_other_id");
    rm("flow_started_at");
  };

  const clearMyInfoSessionOnly = () => {
    Cookies.remove("latest_session_id", { path: "/" });
  };

  const isCrmState = (state) =>
    typeof state === "string" && state.startsWith(CRM_STATE_PREFIX) && !hasNetrustTrialContext(state);

  useEffect(() => {
    const currentURL = new URL(window.location.href);
    const code = currentURL.searchParams.get("code");
    const state = currentURL.searchParams.get("state");
    const error = currentURL.searchParams.get("error");

    if (isPropertyCoOwnerCallback) return;
    if (isCrmState(state)) {
      return;
    }

    const context = Cookies.get("support_sync_context");
    if (!context || hasNetrustTrialContext(state)) {
      return;
    }

    if (code || state || error) {
      history.replace(`/cs-dashboard${window.location.search}`);
    }
  }, [history, isPropertyCoOwnerCallback]);

  // ---- Layout effects (unchanged) ----
  useEffect(() => {
    if (screenWidth > 1200) setPrivacybar(true);
  }, []);
  useEffect(() => {
    if (screenWidth < 1200) setPrivacybar(false);
  }, [screenWidth]);
  useEffect(() => {
    const handleResize = () => {
      setScreenWidth(window.innerWidth);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // ---- Borrower and CRM API calls ----
  const saveUserMyInfoData = async () => {
    // Borrower (normal) flow save
    try {
      setBusyConfirm(true);
      const response = await fetch(
        "https://www.smart-lend.com/api/myinfo/saveUserMyInfoData",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${Cookies.get("token")}`,
          },
          body: JSON.stringify({
            session_id: trialSessionId || Cookies.get("latest_session_id"),
            myinfo_data: myinfoData,
          }),
        }
      );
      const json = await response.json();
      if (response.status === 200) {
        const isMoneyLenderCampaign = Boolean(Cookies.get(MONEY_LENDER_CAMPAIGN_COOKIE));

        clearMyInfoSessionOnly(); // 🔐 clear session id
        clearCoOwnerCookies(); // 🔐 just in case they existed

        if (isMoneyLenderCampaign) {
          sessionStorage.setItem("showCampaignPersonalLoanPrompt", "true");
        } else {
          sessionStorage.setItem("showPersonalLoanPrompt", "true");
        }

        history.push("/b-dashboard");
      } else {
        setMyinfoError("GENERIC");
      }
    } catch (error) {
      console.error(error);
      setMyinfoError("GENERIC");
    } finally {
      setBusyConfirm(false);
    }
  };

  const confirmCrmSync = async () => {
    try {
      if (!crmSync?.sync_id || !crmSync?.confirmation_token) {
        setMyinfoError("CRM_GENERIC");
        return;
      }

      setBusyConfirm(true);
      const response = await fetch(
        "https://www.smart-lend.com/api/crm/sync/confirm",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            sync_id: crmSync.sync_id,
            confirmation_token: crmSync.confirmation_token,
          }),
        }
      );
      const json = await response.json();

      if (response.ok && json?.redirect_url) {
        window.location.href = json.redirect_url;
        return;
      }

      setMyinfoError("CRM_GENERIC");
    } catch (error) {
      console.error(error);
      setMyinfoError("CRM_GENERIC");
    } finally {
      setBusyConfirm(false);
    }
  };

  const resolveUserToken = async (code, session_id, state) => {
    // Borrower (normal) flow resolver
    try {
      const trial = netrustTrialResolveRequest("myinfo", code, state);
      const response = await fetch(
        trial?.url || "https://www.smart-lend.com/api/resolveMyinfoToken",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${Cookies.get("token")}`,
          },
          body: JSON.stringify(trial?.body || { code, session_id }),
        }
      );
      const json = await response.json();
      if (response.status === 200 && !json.errorMessage) {
        if (trial) setTrialSessionId(trial.sessionId);
        setMyinfoData(getMyInfoData(json));
      } else {
        setMyinfoError("GENERIC");
      }
    } catch (error) {
      console.error(error);
      setMyinfoError("GENERIC");
    }
  };

  const failCrmSync = async (state, error) => {
    try {
      const response = await fetch(
        "https://www.smart-lend.com/api/crm/sync/fail",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ state, error }),
        }
      );
      const json = await response.json();

      if (response.ok && json?.redirect_url) {
        window.location.href = json.redirect_url;
        return;
      }
    } catch (err) {
      console.error(err);
    }

    setMyinfoError("CRM_GENERIC");
  };

  const resolveCrmSyncCallback = async (code, state, error) => {
    if (error) {
      failCrmSync(state, error);
      return;
    }

    if (!code || !state) {
      setMyinfoError("CRM_GENERIC");
      return;
    }

    try {
      const response = await fetch(
        "https://www.smart-lend.com/api/crm/sync/resolve",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ code, state }),
        }
      );
      const json = await response.json();

      if (!response.ok || !json?.data || !json?.confirmation_token) {
        setMyinfoError("CRM_GENERIC");
        return;
      }

      setCrmSync({
        sync_id: json.sync_id,
        data_source: json.data_source,
        external_reference: json.external_reference,
        confirmation_token: json.confirmation_token,
      });
      setCrmEntityData(json.data?.entity_info || json.data?.entity || null);
      setCrmBusinessData(json.data);
      setMyinfoData(getMyInfoData(json.data));
    } catch (err) {
      console.error(err);
      setMyinfoError("CRM_GENERIC");
    }
  };

  // Kick off resolve on landing from MyInfo
  useEffect(() => {
    const currentURL = new URL(window.location.href);
    const code = currentURL.searchParams.get("code");
    const state = currentURL.searchParams.get("state");
    const error = currentURL.searchParams.get("error");
    const session_id = Cookies.get("latest_session_id");

    if (isPropertyCoOwnerCallback) return;
    if (isCrmState(state)) {
      resolveCrmSyncCallback(code, state, error);
      return;
    }

    if (Cookies.get("support_sync_context") && !hasNetrustTrialContext(state)) return;

    // Retire callbacks started from old co-owner invitations without resolving
    // or saving provider data. Remove the callback code from the URL as well.
    if (isOtherOwnerFlow) {
      clearMyInfoSessionOnly();
      clearCoOwnerCookies();
      history.replace("/proplconfirmation");
      return;
    }

    if (error) {
      setMyinfoError("GENERIC");
      return;
    }
    if (code && state && (session_id || hasNetrustTrialContext(state))) {
      resolveUserToken(code, session_id, state);
    }
  }, []); // eslint-disable-line

  // ---- UI helpers (unchanged from your version) ----
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const parts = dateString.split(/[-\/]/);
    let date;
    if (parts.length === 3) {
      const [year, day, month] = parts;
      date = new Date(`${year}-${month}-${day}`);
    } else {
      date = new Date(dateString);
    }
    if (!isValid(date)) return "-";
    return format(date, "dd MMMM yyyy");
  };

  function formatYearMonth(input) {
    const [year, month] = (input || "").split("-");
    const date = new Date(year, (month || 1) - 1);
    if (!isValid(date)) return "-";
    return date.toLocaleString("en-US", { month: "long", year: "numeric" });
  }

  const formatAddress = (data) => {
    const formattedAddress = data?.floor?.value
      ? (data?.unit?.value !== undefined ? `Unit ${data?.unit?.value}, ` : "") +
        (data?.floor?.value !== undefined
          ? `Floor ${data?.floor?.value}, `
          : "") +
        (data?.block?.value !== undefined
          ? `Block ${data?.block?.value}, `
          : "") +
        (data?.street?.value !== undefined ? `${data?.street?.value}, ` : "") +
        (data?.postal?.value !== undefined ? `${data?.postal?.value}, ` : "") +
        (data?.country?.desc !== undefined ? `${data?.country?.desc}.` : "")
      : (data?.block?.value !== undefined ? `${data?.block?.value}, ` : "") +
        (data?.street?.value !== undefined ? `${data?.street?.value}, ` : "") +
        (data?.postal?.value !== undefined ? `${data?.postal?.value}, ` : "") +
        (data?.country?.desc !== undefined ? `${data?.country?.desc}.` : "");
    return formattedAddress;
  };

  const isCrmCallback = isCrmState(
    new URLSearchParams(window.location.search).get("state")
  );

  if (isPropertyCoOwnerCallback) return <PropertyCoOwnerMyinfo />;

  if (isOtherOwnerFlow && !isCrmCallback) return <PropLoanConfirmation />;

  return (
    <CrmSyncReview
      flow={isCrmCallback ? "crm" : "borrower"}
      dataSource={crmSync?.data_source || "myinfo"}
      entityData={crmEntityData}
      businessData={crmBusinessData}
      personData={myinfoData}
      error={myinfoError}
      busy={busyConfirm}
      onConfirm={isCrmCallback ? confirmCrmSync : saveUserMyInfoData}
      formatAddress={formatAddress}
      formatDate={formatDate}
      formatYearMonth={formatYearMonth}
      showPrivacyNotice={privacyBar}
      onDismissPrivacyNotice={() => setPrivacybar(false)}
    />
  );
};

export default BauthConf;
